import React, { Component } from "react";
import ReactDOM from "react-dom";
import client, { makeGet } from "@culturehq/client";

import Suggestion from "./Suggestion";

const animations = {
  in: {
    transform: "scale(1)",
    transition: "all 0.25s cubic-bezier(.3, 1.2, .2, 1)"
  },
  out: {
    transform: "scale(0)",
    transition: "all 0.35s cubic-bezier(.3, 1, .2, 1)"
  }
};

const makePortalStyle = (search, container, isPostModal) => {
  if (!search || !container) return {};

  const searchRect = search.getBoundingClientRect();
  const parentRect = container.getBoundingClientRect();
  const selectTagsHeight = container.parentNode.querySelector(".Select-Tags__Container")?.offsetHeight || 0;

  const getTop = () => {
    const defaultTop = `${searchRect.top - parentRect.top + 35}px`;
    const postModalTop = `${searchRect.top - 200 + 35}px`;
    const hiddenTextTop = `${(searchRect.top / parentRect.height) * 100 - selectTagsHeight}px`;

    if (isPostModal) return postModalTop;

    if (searchRect.bottom > parentRect.bottom) return hiddenTextTop;

    return defaultTop;
  };

  return {
    left: `${searchRect.left - parentRect.left + 20}px`,
    top: getTop(),
    transformOrigin: "1em 0%",
    ...animations.out
  };
};

class Suggestions extends Component {
  search = React.createRef();

  state = {
    portalStyle: {},
    selected: null,
    suggestions: [],
    unsubscribe: null
  };

  componentDidMount() {
    this.componentIsMounted = true;

    const { decoratedText, onDecoratorSubscribe, isPostModal, containerRef } = this.props;

    this.setState({
      portalStyle: makePortalStyle(this.search.current, containerRef.current, isPostModal),
      unsubscribe: onDecoratorSubscribe({
        onUpArrow: this.handleUpArrow,
        onDownArrow: this.handleDownArrow,
        onReturn: this.handleReturn
      })
    });

    this.enqueueSearch(decoratedText.slice(1));
  }

  componentDidUpdate(prevProps) {
    const { decoratedText } = this.props;

    if (decoratedText !== prevProps.decoratedText) {
      this.enqueueSearch(decoratedText.slice(1));
    }
  }

  componentWillUnmount() {
    this.componentIsMounted = false;
    const { unsubscribe } = this.state;

    unsubscribe();
  }

  linkedinUserClient = async query => {
    if (query.length < 3 || query.length > 100) return { entities: [] };

    const { users, organizations } = await makeGet("/linkedin/search", { query });

    return {
      entities: users.concat(organizations)
        .sort((a, b) => {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        })
    };
  }

  getSuggestedEntities = query => {
    // eslint-disable-next-line react/destructuring-assignment
    const { suggestionStrategy = "default" } = this.props;
    const strategies = {
      linkedin: () => this.linkedinUserClient(query),
      default: () => client.autocompleteMentions({ query }),
      none: () => () => {}
    };
    const strategy = strategies[suggestionStrategy.toLowerCase()] || strategies.none;
    return strategy();
  }

  performSearch = async query => {
    const { entities } = await this.getSuggestedEntities(query);
    if (!entities || !this.componentIsMounted) return;

    const animation = entities.length > 0 ? animations.in : animations.out;

    this.setState(({ portalStyle }) => ({
      portalStyle: { ...portalStyle, ...animation },
      selected: null,
      suggestions: entities
    }));
  };

  handleUpArrow = () => {
    const { suggestions: prevSuggestions } = this.state;

    if (prevSuggestions.length > 0) {
      this.setState(({ selected, suggestions }) => ({
        selected: ([null, 0].includes(selected) ? suggestions.length : selected) - 1
      }));
      return "handled";
    }

    return "not-handled";
  };

  handleDownArrow = () => {
    const { suggestions: prevSuggestions } = this.state;

    if (prevSuggestions.length > 0) {
      this.setState(({ selected, suggestions }) => ({
        selected: [null, suggestions.length - 1].includes(selected) ? 0 : selected + 1
      }));
      return "handled";
    }

    return "not-handled";
  };

  handleReturn = () => {
    const { onMentionCreated } = this.props;
    const { selected, suggestions } = this.state;

    if (suggestions.length > 0 && selected !== null) {
      onMentionCreated(suggestions[selected]);
      return "handled";
    }

    return "not-handled";
  };

  enqueueSearch(query) {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.timeout = setTimeout(() => this.performSearch(query), 300);
  }

  render() {
    const { children, containerRef, onMentionCreated, suggestionsVisible } = this.props;
    const { portalStyle, selected, suggestions } = this.state;

    if (!containerRef.current || !suggestionsVisible) {
      return <span ref={this.search}>{children}</span>;
    }

    const portal = ReactDOM.createPortal(
      <div className="chq-edi--sg" style={portalStyle}>
        {suggestions.map((suggestion, index) => (
          <Suggestion
            key={`${suggestion.type}/${suggestion.id}`}
            active={selected === index}
            suggestion={suggestion}
            onClick={onMentionCreated}
          />
        ))}
      </div>,
      containerRef.current
    );

    return (
      <>
        <span ref={this.search}>{children}</span>
        {portal}
      </>
    );
  }
}

export default Suggestions;
